/**
 * Comment Templates API Module
 *
 * This module provides API functions for managing comment templates.
 * Uses the customInstance function from auth feature's axios-config for
 * enhanced error handling, retry logic, and cancellation support.
 *
 * All requests automatically include:
 * - JWT Bearer token from localStorage (if available)
 * - Global 401/403 error handling with redirect to login
 * - Automatic retry logic for network/5xx errors
 * - Request cancellation support via promise.cancel()
 */

import { customInstance } from '@/features/auth/axios-config'
import {
  RequestCreateCommentTemplate,
  RequestUpdateCommentTemplate,
  SuccessResponseListResponseCommentTemplate,
} from '@/schema/commentTemplate'

import { CommentTemplateFilters } from './types'

const API_ENDPOINT_COMMENT_TEMPLATES = '/document/v1/templates'
const buildApiById = (id: string) => `${API_ENDPOINT_COMMENT_TEMPLATES}/${id}`

export const commentTemplateApi = {
  /**
   * Fetches paginated list of comment templates with optional filtering and sorting
   * @param data - Optional filter parameters including search query, pagination, and sorting
   * @returns Promise with array of comment templates and total count
   */
  getCommentTemplates: async (
    data?: CommentTemplateFilters
  ): Promise<SuccessResponseListResponseCommentTemplate> => {
    const requestBody: Record<string, unknown> = {}

    if (data?.limit) requestBody.limit = data.limit
    if (data?.offset !== undefined) requestBody.offset = data.offset
    if (data?.sort_by) requestBody.sort_by = data.sort_by
    if (data?.q) requestBody.q = data.q

    return customInstance<SuccessResponseListResponseCommentTemplate>({
      method: 'post',
      url: `${API_ENDPOINT_COMMENT_TEMPLATES}/list`,
      data: requestBody,
    })
  },

  /**
   * Fetches a single comment template by ID
   * @param id - Comment template ID
   * @returns Promise with comment template data
   */
  getCommentTemplateById: async (
    id: string
  ): Promise<SuccessResponseListResponseCommentTemplate> => {
    return customInstance<SuccessResponseListResponseCommentTemplate>({
      method: 'get',
      url: buildApiById(id),
    })
  },

  /**
   * Creates a new comment template
   * @param data - Comment template data with name and content (both required)
   * @returns Promise with created comment template
   */
  createCommentTemplate: async (
    data: RequestCreateCommentTemplate
  ): Promise<SuccessResponseListResponseCommentTemplate> => {
    return customInstance<SuccessResponseListResponseCommentTemplate>({
      method: 'post',
      url: API_ENDPOINT_COMMENT_TEMPLATES,
      data,
    })
  },

  /**
   * Updates an existing comment template with partial data
   * @param id - Comment template ID
   * @param data - Updated comment template data (partial update supported)
   * @returns Promise with updated comment template
   */
  updateCommentTemplate: async (
    id: string,
    data: RequestUpdateCommentTemplate
  ): Promise<SuccessResponseListResponseCommentTemplate> => {
    return customInstance<SuccessResponseListResponseCommentTemplate>({
      method: 'put',
      url: buildApiById(id),
      data,
    })
  },

  /**
   * Deletes a comment template
   * @param id - Comment template ID
   * @returns Promise with deletion confirmation
   */
  deleteCommentTemplate: async (id: string): Promise<void> => {
    return customInstance<void>({
      method: 'delete',
      url: buildApiById(id),
    })
  },
}
